import React, { useState } from 'react';
import './App.css';
import { ResponsiveBannerAd } from './components/AdSenseAd';
import {
  createEmptyHand,
  getTotalTileCount,
  calculateWaitingTiles,
  getTileImage,
  getTileName,
  TILE_IMAGES,
  TILE_NAMES
} from './utils/waitingTileCalculator';

function App() {
  // 状態管理
  const [handTiles, setHandTiles] = useState(createEmptyHand());
  const [waitingResult, setWaitingResult] = useState(null);

  // 牌を追加する関数
  const addTile = (type, index) => {
    const newHand = { ...handTiles };
    const currentCount = type === 'jihai' ? newHand[type][index] : newHand[type][index];
    
    if (currentCount < 4 && getTotalTileCount(newHand) < 13) {
      newHand[type][index]++;
      setHandTiles(newHand);
      
      // 13枚になったら自動で計算実行
      if (getTotalTileCount(newHand) === 13) {
        setTimeout(() => {
          const result = calculateWaitingTiles(newHand);
          setWaitingResult(result);
          
          if (!result.isValid) {
            alert(result.errorMessage || '不明なエラーが発生しました');
          }
        }, 100);
      }
    }
  };

  // 牌を削除する関数
  const removeTile = (type, index) => {
    const newHand = { ...handTiles };
    if (newHand[type][index] > 0) {
      newHand[type][index]--;
      setHandTiles(newHand);
    }
  };

  // 手牌の配列を取得（表示用）
  const getHandTileArray = () => {
    const tiles = [];
    
    // 萬子
    for (let i = 1; i <= 9; i++) {
      for (let count = 0; count < handTiles.manzu[i]; count++) {
        tiles.push({
          type: 'manzu',
          number: i,
          image: TILE_IMAGES.manzu[i],
          id: `manzu-${i}-${count}`
        });
      }
    }
    
    // 筒子
    for (let i = 1; i <= 9; i++) {
      for (let count = 0; count < handTiles.pinzu[i]; count++) {
        tiles.push({
          type: 'pinzu',
          number: i,
          image: TILE_IMAGES.pinzu[i],
          id: `pinzu-${i}-${count}`
        });
      }
    }
    
    // 索子
    for (let i = 1; i <= 9; i++) {
      for (let count = 0; count < handTiles.souzu[i]; count++) {
        tiles.push({
          type: 'souzu',
          number: i,
          image: TILE_IMAGES.souzu[i],
          id: `souzu-${i}-${count}`
        });
      }
    }
    
    // 字牌
    for (let i = 0; i < 7; i++) {
      for (let count = 0; count < handTiles.jihai[i]; count++) {
        tiles.push({
          type: 'jihai',
          jihaiIndex: i,
          image: TILE_IMAGES.jihai[i],
          id: `jihai-${i}-${count}`
        });
      }
    }
    
    return tiles;
  };

  // 手牌から牌を削除（タップで削除）
  const removeTileFromHand = (tileId) => {
    const [type, indexStr] = tileId.split('-');
    const index = parseInt(indexStr);
    
    if (type === 'jihai') {
      removeTile('jihai', index);
    } else {
      removeTile(type, index);
    }
  };

  // 手牌をリセット
  const resetHand = () => {
    setHandTiles(createEmptyHand());
    setWaitingResult(null);
  };

  const currentTileCount = getTotalTileCount(handTiles);

  return (
    <div className="container">
      {/* ヘッダー */}
      <header className="header">
        <h1 className="title">待ち牌ツール</h1>
        <p className="subtitle">麻雀の待ち牌を簡単に計算できる無料ツール</p>
      </header>

      {/* 手牌表示エリア */}
      <main>
      <section className="section" aria-label="手牌入力エリア">
        <div className="hand-header">
          <h2 className="section-title">手牌（{currentTileCount}/13枚）</h2>
          <button className="reset-button" onClick={resetHand} title="リセット">
            ↻
          </button>
        </div>
        <div className="hand-area">
          {getHandTileArray().length === 0 ? (
            <div className="empty-hand-text">牌を選択してください</div>
          ) : (
            <div className="hand-tiles">
              {getHandTileArray().map((tile) => (
                <div
                  key={tile.id}
                  className="hand-tile"
                  onClick={() => removeTileFromHand(tile.id)}
                >
                  <img
                    src={tile.image}
                    alt={TILE_NAMES[tile.type][tile.number || tile.jihaiIndex]}
                    className="hand-tile-image"
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* 結果表示 */}
      {waitingResult && (
        <section className="section" aria-label="待ち牌分析結果">
          <h2 className="section-title">分析結果</h2>
          
          {waitingResult.isValid && waitingResult.waitingTiles.length > 0 ? (
            <>
              <div className="result-header">
                <p className="result-title">
                  待ち牌：{waitingResult.waitingTiles.length}種類
                </p>
              </div>
              
              <div className="waiting-tiles-container">
                {waitingResult.waitingTiles.map((tile, index) => (
                  <div key={index} className="waiting-tile">
                    <img
                      src={getTileImage(tile)}
                      alt={getTileName(tile)}
                      className="waiting-tile-image"
                    />
                    <p className="waiting-tile-name">{getTileName(tile)}</p>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="no-result-container">
              <span className="no-result-icon">ⓘ</span>
              <p className="no-result-text">
                {waitingResult.errorMessage || '待ち牌がありません'}
              </p>
            </div>
          )}
        </section>
      )}

      {/* 結果後AdSense広告 */}
      {waitingResult && <ResponsiveBannerAd position="content" />}

      {/* 牌選択エリア */}
      <section className="section" aria-label="牌選択エリア">
        <h2 className="section-title">牌を選択（タップして手牌に追加）</h2>
        
        {/* 萬子 */}
        <h3 className="sub-title">萬子</h3>
        <div className="tile-selection-row">
          {[1,2,3,4,5,6,7,8,9].map(num => (
            <div
              key={`manzu-${num}`}
              className={`selectable-tile ${
                (handTiles.manzu[num] >= 4 || currentTileCount >= 13) ? 'disabled' : ''
              }`}
              onClick={() => addTile('manzu', num)}
            >
              <img
                src={TILE_IMAGES.manzu[num]}
                alt={TILE_NAMES.manzu[num]}
                className="selectable-tile-image"
              />
              {handTiles.manzu[num] > 0 && (
                <div className="tile-count-badge">
                  {handTiles.manzu[num]}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* 筒子 */}
        <h3 className="sub-title">筒子</h3>
        <div className="tile-selection-row">
          {[1,2,3,4,5,6,7,8,9].map(num => (
            <div
              key={`pinzu-${num}`}
              className={`selectable-tile ${
                (handTiles.pinzu[num] >= 4 || currentTileCount >= 13) ? 'disabled' : ''
              }`}
              onClick={() => addTile('pinzu', num)}
            >
              <img
                src={TILE_IMAGES.pinzu[num]}
                alt={TILE_NAMES.pinzu[num]}
                className="selectable-tile-image"
              />
              {handTiles.pinzu[num] > 0 && (
                <div className="tile-count-badge">
                  {handTiles.pinzu[num]}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* 索子 */}
        <h3 className="sub-title">索子</h3>
        <div className="tile-selection-row">
          {[1,2,3,4,5,6,7,8,9].map(num => (
            <div
              key={`souzu-${num}`}
              className={`selectable-tile ${
                (handTiles.souzu[num] >= 4 || currentTileCount >= 13) ? 'disabled' : ''
              }`}
              onClick={() => addTile('souzu', num)}
            >
              <img
                src={TILE_IMAGES.souzu[num]}
                alt={TILE_NAMES.souzu[num]}
                className="selectable-tile-image"
              />
              {handTiles.souzu[num] > 0 && (
                <div className="tile-count-badge">
                  {handTiles.souzu[num]}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* 字牌 */}
        <h3 className="sub-title">字牌</h3>
        <div className="tile-selection-row">
          {TILE_NAMES.jihai.map((_, index) => (
            <div
              key={`jihai-${index}`}
              className={`selectable-tile ${
                (handTiles.jihai[index] >= 4 || currentTileCount >= 13) ? 'disabled' : ''
              }`}
              onClick={() => addTile('jihai', index)}
            >
              <img
                src={TILE_IMAGES.jihai[index]}
                alt={TILE_NAMES.jihai[index]}
                className="selectable-tile-image"
              />
              {handTiles.jihai[index] > 0 && (
                <div className="tile-count-badge">
                  {handTiles.jihai[index]}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>
      </main>

      {/* 使い方説明 */}
      <section className="section" aria-label="使い方説明">
        <h2 className="section-title">使い方</h2>
        <div className="help-container">
          <div className="help-item">
            <span className="help-item-icon">ⓘ</span>
            <p className="help-item-text">
              13枚の手牌を入力して待ち牌を計算できます
            </p>
          </div>
          <div className="help-item">
            <span className="help-item-icon">⊞</span>
            <p className="help-item-text">
              牌をタップして手牌に追加、手牌の牌をタップして削除
            </p>
          </div>
        </div>
      </section>

      {/* フッターAdSense広告 */}
      <ResponsiveBannerAd position="footer" />
    </div>
  );
}

export default App;
